import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Paper,
  Divider,
} from '@mui/material';
import {
  PersonRounded,
  LocalHospitalRounded,
  HealingRounded,
  MedicationRounded,
  WarningAmberRounded,
  PeopleRounded,
  CheckCircleRounded,
  RadioButtonUncheckedRounded,
  ChevronRightRounded,
  QrCode2Rounded,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useAuth } from 'hooks/useAuth';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import { masterChartPath } from 'constants/masterChartRoutes';

const CATEGORIES = [
  {
    id: 'demographics',
    label: 'Demographics',
    description: 'Date of birth, sex, and weight.',
    icon: PersonRounded,
    paletteColor: 'primary',
    route: masterChartPath('demographics'),
    isComplete: (hr) => !!(hr?.dateOfBirth && hr?.sex),
  },
  {
    id: 'conditions',
    label: 'Medical Conditions',
    description: 'Chronic conditions and diagnoses.',
    icon: LocalHospitalRounded,
    paletteColor: 'info',
    route: masterChartPath('conditions'),
    isComplete: (hr) => (hr?.pmh?.length ?? 0) > 0,
  },
  {
    id: 'surgical',
    label: 'Surgical History',
    description: 'Previous surgeries and procedures.',
    icon: HealingRounded,
    paletteColor: 'error',
    route: masterChartPath('surgical-history'),
    isComplete: (hr) => (hr?.psh?.length ?? 0) > 0,
  },
  {
    id: 'medications',
    label: 'Medications',
    description: 'Current medications and dosages.',
    icon: MedicationRounded,
    paletteColor: 'success',
    route: '/dashboard/medications',
    isComplete: (_, medCount) => medCount > 0,
  },
  {
    id: 'allergies',
    label: 'Allergies',
    description: 'Drug, food, and environmental allergies.',
    icon: WarningAmberRounded,
    paletteColor: 'warning',
    route: masterChartPath('allergies'),
    isComplete: (hr) => (hr?.allergies?.length ?? 0) > 0,
  },
  {
    id: 'social',
    label: 'Other History',
    description: 'Lifestyle factors and family history.',
    icon: PeopleRounded,
    paletteColor: 'secondary',
    route: masterChartPath('other-history'),
    isComplete: (hr) => {
      const other = hr?.other;
      if (!other) return false;
      return Object.values(other).some((v) => v && String(v).trim() !== '');
    },
  },
];

const RecordRow = ({ category, complete, to, linkState }) => {
  const theme = useTheme();
  const palette = theme.palette[category.paletteColor] ?? theme.palette.primary;
  const Icon = category.icon;

  return (
    <ListItemButton
      component={RouterLink}
      to={to}
      state={linkState}
      sx={{ py: 1.5, px: 2 }}
    >
      <ListItemIcon sx={{ minWidth: 44 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: alpha(palette.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 20, color: `${category.paletteColor}.main` }} />
        </Box>
      </ListItemIcon>
      <ListItemText
        primary={category.label}
        secondary={category.description}
        primaryTypographyProps={{ fontWeight: 500, variant: 'body2' }}
        secondaryTypographyProps={{ variant: 'caption' }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
        {complete ? (
          <CheckCircleRounded sx={{ fontSize: 18, color: 'primary.main' }} />
        ) : (
          <RadioButtonUncheckedRounded sx={{ fontSize: 18, color: 'grey.400' }} />
        )}
        <ChevronRightRounded sx={{ fontSize: 18, color: 'grey.400' }} />
      </Box>
    </ListItemButton>
  );
};

const HealthRecordsHub = () => {
  const navigate = useNavigate();
  const { userData, medications } = useAuth();

  const healthRecords = userData?.healthRecords ?? {};
  const medicationCount = medications?.length ?? 0;
  const completedCount = CATEGORIES.filter((cat) =>
    cat.isComplete(healthRecords, medicationCount)
  ).length;
  const pct = Math.round((completedCount / CATEGORIES.length) * 100);

  return (
    <div className="inner">
      <DashboardPageHeader
        title="MasterChart"
        subtitle="Your personal health record, always up to date."
        actions={
          <Button
            variant="contained"
            startIcon={<QrCode2Rounded />}
            onClick={() => navigate(masterChartPath('share'))}
          >
            Share with provider
          </Button>
        }
      />

      {/* Completion progress */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
          <Typography variant="caption" color="text.secondary">
            Profile completion
          </Typography>
          <Typography variant="caption" fontWeight={700} color="primary.main">
            {pct}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { transition: 'none' } }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {completedCount} of {CATEGORIES.length} sections complete
        </Typography>
      </Paper>

      {/* Health record sections */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <List disablePadding>
          {CATEGORIES.map((cat, i) => (
            <React.Fragment key={cat.id}>
              {i > 0 && <Divider component="li" />}
              <RecordRow
                category={cat}
                complete={cat.isComplete(healthRecords, medicationCount)}
                to={cat.route}
                linkState={
                  cat.id === 'medications' ? { from: 'masterchart' } : undefined
                }
              />
            </React.Fragment>
          ))}
        </List>
      </Paper>
    </div>
  );
};

export default HealthRecordsHub;
